import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BulkUpdateResponse, IGenericResponse } from '@app/core/bsshapi/api-wrappers';
import { ICollaborator, ICollaboratorListResponse, IShareUrlResponse } from '@app/core/model/collaborator';
import { DownloadUrl } from '@app/core/model/download-url';
import IResource from '@app/core/model/resource';
import { ResourceType } from '@app/core/model/resource-type';
import { IRun } from '@app/core/model/runs/run';
import { IV1ApiResponse, IV1ListResponse } from '@app/core/model/v1-api/v1-api-wrappers';
import { Invitation } from '@app/core/model/v2-api/invitation';
import { IApiListResponseWrapper, IApiResponseWrapper, IRequeueableResponse, IUnzipRunResponse } from '@app/core/model/v2-api/v2-api-wrappers';
import { genericRetryWhen, observableEmitDelay } from '@app/core/rxjsutils/rxjs-utilities';
import { DownloadService } from '@app/core/services/toolbar/download.service';
import { RestoreService } from '@app/core/services/toolbar/restore.service';
import { ShareService } from '@app/core/services/toolbar/share.service';
import { TransferOwnershipService } from '@app/core/services/toolbar/transfer-ownership.service';
import { IRunsState } from '@app/core/store/runs/runs.state';
import { BasespaceService, V2FilesList, V2SequencingStats, V2AppSessionCompactList, V1pre3ProjectCompact } from '@bssh/ng-sdk';
import { RequeueService } from '@app/core/services/requeue/requeue.service';
import environment from '@environments/environment';
import { NON_DOWNLOADABLE_RUN_STATUSES, RunStatus } from '@app/core/model/runs/run-status';
import { Observable, Subject, throwError, EMPTY, BehaviorSubject, of, forkJoin } from 'rxjs';
import { catchError, delay, finalize, map, retryWhen, switchMap } from 'rxjs/operators';
import { DeleteOption } from '@app/core/model/delete-options';
import { ArchiveService } from '@app/core/services/toolbar/archive.service';
import { RunEditService } from '@app/runs/services/run-edit.service';
import { TrashService } from '@app/core/services/toolbar/trash.service';
import { isNullOrUndefined } from 'util';
import { PageContextService } from '@app/core/services/page-context.service';
import { IResourceStore, ITransferrableResource } from '../resource/resource.store';
import { ArrayUtilities } from '@app/core/utilities/array-utilities';
import { BsApiService } from '@app/core/services/bs-api/bs-api-service';
import { ErrorMessages } from '@app/core/utilities/error-messages';
import { ToastrService } from '@bssh/comp-lib';
import { ToastrMessages } from '@app/core/utilities/toastr-messages';
import { ModalTexts } from '../../../shared/modals/modal-texts';
import { IUser } from '../../model/user';
import { ARCHIVABLE_RUN_STATUSES } from '../../model/runs/run-status';
import { ResourceCommonUtilityStore } from '../resource/resource.common.utlility.store';
import { Constants } from '@app/core/utilities/constants';
import { RunUnzipService } from '@app/runs/services/run-unzip.service';
import { isEmpty } from 'lodash';
import { TransformSampleSheetResponse, ValidateSampleSheetResponse } from '@stratus/gss-ng-sdk';
import { CodeFeaturesService } from '@app/core/services/user/code-features.service';
import { BsSpinnerService } from '@app/core/services/bs-spinner/bs-spinner.service';
import { InstrumentPlatforms, InstrumentTypes } from '@app/core/model/runs/instrument';
import { FileStatus } from '@app/core/model/file-status';
import { GssApiService } from '@app/run-planning/services/gss-api-service';

// Interface
export interface IRunsStore extends IResourceStore<IRunsState> {
  loadCurrentRun(id: string, forceLoad: boolean): void;
  loadRunsList(offset: number, limit: number, sortby: string, sortdir: string, forceLoad: boolean): void;
  loadInstrumentFilterOptions(): void;
  edit(newExperimentName: string): void;
  canRequeue(): boolean;
  openRequeueSampleSheetPage();
  updateSelectedRuns(runs: IRun[]): void;
  listRunsFiles(): Observable<V2FilesList>;
  loadRunsSequencingStats(): Observable<V2SequencingStats>;
  unloadCurrentRun(): void;
  canUnzipRun(): boolean;
  unzipRun(): void;
  getRunById(id: string, includeSequencingStats: boolean): Observable<IRun>;
}

@Injectable({
  providedIn: 'root'
})

export class RunsStore extends ResourceCommonUtilityStore<IRunsState> implements IRunsStore, ITransferrableResource {


  private v1RunsListEndpoint = `${environment.apiUrl}/v1pre3/users/current/runs`;
  // pass in the state slices we are interested in...

  private refreshSubject = new BehaviorSubject<boolean>(false);
  runsListRefreshed$ = this.refreshSubject.asObservable();

  // Basically all instruments that are older than MiSeqi100Series
  private readonly DEMUX_UNSUPPORTED_INSTRUMENT_PLATFORMS = [
    InstrumentPlatforms.UNKNOWN,
    InstrumentPlatforms.NEXTSEQ,
    InstrumentPlatforms.MISEQ,
    InstrumentPlatforms.HISEQ,
    InstrumentPlatforms.ECO,
    InstrumentPlatforms.ISCAN,
    InstrumentPlatforms.HISEQ_X,
    InstrumentPlatforms.NEO_PREP,
    InstrumentPlatforms.MINISEQ,
    InstrumentPlatforms.NOVASEQ,
    InstrumentPlatforms.ISEQ,
    InstrumentPlatforms.TRUSIGHT_NIPT,
    InstrumentPlatforms.NEXTSEQ_1K_2K,
    InstrumentPlatforms.NOVASEQ_X_SERIES
  ];

  constructor(private basespaceApi: BasespaceService, private httpClient: HttpClient,
              private downloadService: DownloadService, private restoreService: RestoreService,
              private archiveService: ArchiveService, private editService: RunEditService,
              private shareService: ShareService, private trashService: TrashService,
              private transferOwnershipService: TransferOwnershipService,
              private unzipService: RunUnzipService, private pageContextService: PageContextService,
              private bsApi: BsApiService, private toastrService: ToastrService,
              private requeueService: RequeueService, private codeFeatureService: CodeFeaturesService,
              public injector: Injector, private bsSpinnerService: BsSpinnerService,
              private gssApiService: GssApiService,) {

    super(['runsList', 'runsOffset', 'runsLimit', 'runsSortBy', 'runsSortDir', 'selectedRuns',
      'currentRun', 'currentUser', 'currentUserId', 'currentUserCodeFeatures', 'runStateError',
      'instrumentFilterOptions'], injector);

  }

  getResourceId(resource: IResource): string {
    return (resource as IRun).V1Pre3Id;
  }

  /**
   * Calls BSSH API to load run details and cache in the run store
   * @param id Id of the run to fetch. Will not fetch if the state's currentRun already matches this id.
   */
  loadCurrentRun(id: string, refreshState: boolean = false) {
    // TODO handle 403s/404s (for data you don't own)

    const currentState = this.getState();
    if (currentState && currentState.currentRun && currentState.currentRun.Id === id && !refreshState) {
      // if run already set don't fetch, but dispatch current state if another call is made later
      this.dispatchCurrentState(currentState);
    } else {
      this.loadingSubject.next(true);
      // return this.basespaceApi.GetV2UsersCurrent(this.getUserCurrentRequest());
      const runDetails$ = this.basespaceApi.GetV2RunsId({ id, include: ['sequencingstats', 'RelatedUserWithAccess', 'DataStatuses'] });

      // Subscribe to retrieve
      this.subs.sink = runDetails$.pipe(
        // Retry in case of HTTP errors
        retryWhen(genericRetryWhen()),
        // To avoid a Flash of content, maintain a delay
        delay(observableEmitDelay),
        // Stop loading in finalize.
        finalize(() => this.loadingSubject.next(false))
      ).subscribe({
        next: (v2runDetails) => {
          this.setState({ currentRun: v2runDetails }, RunStoreActions.LoadRunDetails);
        },
        error: error => this.handleError(error, () => ({ ...currentState, runStateError: error }))
      });

    }

  }

  /**
   * Gets a Run by Id without mutating the Store's state. If the current context run is the same as the one requested, returns from state
   * Otherwise calls API to obtain it.
   * @param id Run Id
   */
  getRunById(id: string, includeSequencingStats: boolean = false): Observable<IRun> {
    const currentState = this.getState();
    if (currentState && currentState.currentRun && currentState.currentRun.Id === id) {
      return of( currentState.currentRun);
    } else {
      return this.basespaceApi.GetV2RunsId({ id, include: includeSequencingStats ? ['sequencingstats'] : null });
    }
  }

  /**
   * Unload current run data to avoid stale data displayed when enter another run details page
   */
  unloadCurrentRun() {
    this.setState({ currentRun: null }, RunStoreActions.UnloadCurrentRun);
  }

  /**
   * populating V1Pre3Id with Id of runs
   * @param runs: runs
   */
  private populateV1Pre3Id(runs: IRun[]): IRun[] {
    runs.forEach(run => run.V1Pre3Id = run.Id);
    return runs;
  }

  /**
   * Calls BSSH Api and cache the list of instruments for column filter.
   */
  loadInstrumentFilterOptions() {
    this.loadingSubject.next(true);
    this.subs.sink = this.bsApi.getInstrumentList(1000, 'Id')
      .pipe(
        map(res => res.Items),
        map(items => {
          return items.map(instrument => {
            return { displayName: instrument.Id, filterValue: instrument.Id };
          })
        }),
        finalize(() => this.loadingSubject.next(false))
      ).subscribe({
        next: filterOptions => {
          this.setState({ instrumentFilterOptions: filterOptions }, RunStoreActions.UpdateInstrumentFilterList);
        },
        error: error => {
          this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        }
      });
  }

  /**
   * Calls BSSH API to load a list of runs and cache in the run store as 'runsList'
   * @param id Id of the run to fetch. Will not fetch if the state's currentRun already matches this id.
   */
  loadRunsList(offset: number = 0, limit: number = 10, sortby: string = 'DateCreated',
    sortdir: string = 'Desc', forceLoad: boolean = false, ownedbycurrentuser?: boolean) {

    const currentState = this.getState();
    if (currentState && currentState.runsOffset === offset && currentState.runsLimit === limit
      && currentState.runsSortBy === sortby && currentState.runsSortDir === sortdir && !forceLoad) {

      // if same settings, use the list we already have cached
      this.dispatchCurrentState(currentState);
    } else {
      this.loadingSubject.next(true);

      const requestUrl = this.v1RunsListEndpoint;
      const requestParams: any = { includeLocked: true, offset, limit, sortby, sortdir };

      if (ownedbycurrentuser !== undefined) {
        requestParams.ownedbycurrentuser = ownedbycurrentuser;
      }

      this.subs.sink = this.httpClient.get<IV1ApiResponse<IV1ListResponse<IRun>>>(requestUrl,
        {
          params: requestParams,
          headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' }),
          withCredentials: true
        })
        .pipe(
          // Retry in case of HTTP errors
          retryWhen(genericRetryWhen()),
          // To avoid a Flash of content, maintain a delay
          delay(observableEmitDelay),
          // Stop loading in finalize.
          finalize(() => this.loadingSubject.next(false))
        ).subscribe({
          next: (apiResponse) => {
            const runsList = this.populateV1Pre3Id(apiResponse.Response.Items);

            // save the list and the paging params
            // TODO also filters, when that feature is ready
            this.setState({
              runsList,
              runsOffset: apiResponse.Response.Offset,
              runsLimit: apiResponse.Response.Limit,
              runsSortBy: apiResponse.Response.SortBy,
              runsSortDir: apiResponse.Response.SortDir,
              runStateError: null,
              runsTotalCount: apiResponse.Response.TotalCount,
              selectedRuns: null
            }, RunStoreActions.LoadRunsList);
            this.loadingSubject.next(false);
          },
          error: error => {
            this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
            this.loadingSubject.next(false);
          }
        });
    }
  }

  updateSelectedRuns(updatedSelections: IRun[]) {
    const runs = this.populateV1Pre3Id(updatedSelections);
    this.setState({ selectedRuns: runs }, RunStoreActions.UpdateSelectedRuns);
  }

  /**
   * get list of runs based on page context.
   */
  getActiveList(): IRun[] {
    const { currentRun, selectedRuns } = this.getState();

    if (this.pageContextService.isRunMasterList()) {
      return selectedRuns;
    }

    if (isNullOrUndefined(currentRun)) {
      return null;
    }

    return [currentRun];
  }

  /**
   * set state based on page context. Update runlist and selected runs if page context is
   * master list.
   * Updates runlist, selected runs and the current run if page context is
   * details page.
   * @param propertyChange: property that are changing
   * @param run: the run acted upon
   * @param actionName: name of action based on service
   * @param error: error in case of partial success ( i.e in case of list of resources)
   * @param forceRefreshRunsList: whether to refresh runs list if action was performed from list page
   */
  private setStateOnSuccess(propertyChange: object, runs: IRun[], actionName: RunStoreActions,
    error: any = null, forceRefreshRunsList?: boolean) {

    const { runsList, selectedRuns, currentRun } = this.getState();

    let newCurrentRun = null; // currentRun null if in any other page context than details
    let newRunsList = runsList;
    let newSelectedRunsList = selectedRuns;

    if (this.pageContextService.isRunMasterList()) {
      if (isNullOrUndefined(runsList) || isNullOrUndefined(selectedRuns) || selectedRuns.length === 0) {
        return;
      }

      runs.forEach(run => {
        newRunsList = this.getUpdatedResourceList(run, runsList, propertyChange);
        newSelectedRunsList = this.getUpdatedResourceList(run, selectedRuns, propertyChange);
      });
    }

    if (this.pageContextService.isRunDetails()) {
      if (runs.length === 1) {
        newCurrentRun = Object.assign(runs[0], propertyChange);
      } else {
        newCurrentRun = currentRun;
      }
    }

    this.setState({
      runsList: newRunsList,
      selectedRuns: newSelectedRunsList,
      currentRun: newCurrentRun,
      runStateError: error
    },
      actionName);
    this.refreshSubject.next(forceRefreshRunsList);
  }

  isTransferInProgress(): boolean {
    const activeRuns = this.getActiveList();

    if (ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return activeRuns[0].IsTransferInProgress ? true : false;
    }

    return false;
  }

  /**
   * Determines whether the currentUser can edit the currentRun, according the store's state.
   */
  canEdit(): boolean {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return false;
    }

    const { currentUserId } = this.getState();
    return this.editService.canEditRun(activeRuns[0], currentUserId);
  }

  /**
   * Calls BSSH API to edit a run's experiment name. Only called from the run-details pages, need not handle other page-contexts
   * @param newExperimentName the new value of the experimentName field
   */
  edit(newExperimentName: string): void {
    // Sadly, v2 post-runs-id doesn't support the UI's only use case: experimentName editing :(
    // note: have to use v1Pre3Id throughout to call v1 APIs
    if (!this.canEdit()) {
      // re-check, make no state change if edit run name not allowed.
      return;
    }

    this.loadingSubject.next(true);
    const activeRuns = this.getActiveList();
    const { currentUserId } = this.getState();

    if (!this.isRunOwner(currentUserId, activeRuns[0])) {
      // re-check, make no state change if currentUser isn't the run owner.
      // button should be disabled before we reach this code, however, just a simple illustration.
      this.loadingSubject.next(false);
      return;
    }

    this.subs.sink = this.editService.editRunName(newExperimentName, activeRuns[0], currentUserId).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      // TODO define v1-response-wrapper interface. use any for now
      next: (apiResponse: IApiResponseWrapper<IRun>) => {
        this.setStateOnSuccess({ ExperimentName: apiResponse.Response.ExperimentName },
          activeRuns, RunStoreActions.EditRunExpName, null, this.pageContextService.isRunMasterList());
      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
      }
    });
  }

  /**
   * Determines whether the currentUser can transfer the ownership for currentRun,
   * according the store's state.
   */
  canTransferOwnership(): boolean {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return false;
    }

    if (this.isTransferInProgress()) {
      return false;
    }

    const { currentUser } = this.getState();
    return this.transferOwnershipService.canTransferResourceOwnership(activeRuns[0], currentUser);
  }

  /**
   * Calls BSSH API to transfer a run's ownership.
   * @param email   the email id of user to whom the run ownership is to be transferred
   * @param comment the user comments for performing this action
   * @param domain  the domain of the user to whom the ownership is to be transferred, 
   * optional when transfer within same domain, will be required when cross-domain transfer
   */
  transferOwnership(email: string, comment?: string, domain?: string): void {
    // note: have to use v1Pre3Id throughout to call v1 APIs
    if (!this.canTransferOwnership()) {
      // re-check, make no state change if currentUser cannot transfer the ownership.
      return;
    }

    this.loadingSubject.next(true);
    const activeRuns = this.getActiveList();

    // TODO: check if activeRuns has V1Pre3Id
    this.subs.sink = this.transferOwnershipService.transferOwnership(ResourceType.RUN, activeRuns[0].V1Pre3Id, email, comment, domain).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }))
      .subscribe({
        next: (apiResponse: IApiResponseWrapper<Invitation>) => {
          this.setStateOnSuccess({ IsTransferPending: true }, activeRuns, RunStoreActions.TransferRunOwnership,
            null, this.pageContextService.isRunMasterList());
          this.toastrService.success(ToastrMessages.TRANSFER_INVITATION_SENT);
        },
        error: error => {
          this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        }
      });
  }

  /**
   * Determines whether the currentUser can cancel the ownership transfer request for activeRun,
   * according the store's state.
   */
  canCancelOwnershipTransfer(): boolean {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return false;
    }

    const { currentUser } = this.getState();
    return this.transferOwnershipService.canCancelResourceOwnershipTransfer(activeRuns[0], currentUser);
  }

  /**
   * Fetches the list of pending transfer requests for the given resource
   */
  getPendingTransfers(): Observable<Invitation[]> {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return EMPTY;
    }

    this.loadingSubject.next(true);

    return this.transferOwnershipService.getPendingTransfers(ResourceType.RUN, activeRuns[0].V1Pre3Id).pipe(
      map((response: IApiListResponseWrapper<Invitation>) => response.Response.Items),
      catchError((error: Error) => {
        this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        return throwError(error);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  /**
   * Calls BSSH API to cancel the transfer of a run's ownership. Only called from the run-details pages,
   * need not handle other page-contexts
   */
  cancelInvitation(InvitationId: string): void {
    // note: have to use v1Pre3Id throughout to call v1 APIs

    if (!this.canCancelOwnershipTransfer()) {
      // re-check, make no state change if currentUser cannot transfer the ownership.
      return;
    }

    this.loadingSubject.next(true);
    const activeRuns = this.getActiveList();

    this.subs.sink = this.transferOwnershipService.cancelInvitation(InvitationId).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }))
      .subscribe({
        next: (apiResponse: IApiResponseWrapper<Invitation>) => {
          this.setStateOnSuccess({ IsTransferPending: false }, activeRuns, RunStoreActions.CancelRunTransfer,
            null, this.pageContextService.isRunMasterList());
          this.toastrService.success(ToastrMessages.CANCEL_OWNERSHIP_SUCCESS_MESSAGE);
        },
        error: error => {
          this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        }
      });
  }

  canArchive(): boolean {
    return this.isArchiveEnabled();
  }

  archive(): void {
    const archivableRuns: IRun[] = this.getArchivableResources() as IRun[];
    const archiveObservable = this.archiveResources(archivableRuns);
    this.subs.sink = archiveObservable.subscribe({
      next: (apiResponse: IApiResponseWrapper<BulkUpdateResponse>) => {
        const proccessedResponse = this.processApiResponse(archivableRuns, apiResponse,
          ErrorMessages.FAILED_TO_ARCHIVE_RUNS);
        if (proccessedResponse.totalSuccessCount > 0) {
          // display toast on success
          this.toastrService.success(ModalTexts.ARCHIVE.TOAST_SUCCESS.RUN
            .replace('$SUCCESS_COUNT', apiResponse.Response.TotalSuccessCount.toString()));
        }

        this.setStateOnSuccess({ IsArchived: true, DataStatuses: [FileStatus.ACTIVE, FileStatus.ARCHIVING, FileStatus.ARCHIVED]}, proccessedResponse.successfulResources as IRun[],
           RunStoreActions.ArchiveRun, proccessedResponse.failureError, this.pageContextService.isRunMasterList());
      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
      }
    });
    return;
  }

  getArchiveResponse(resourceIds: string[]): Observable<IApiResponseWrapper<BulkUpdateResponse>> {
    return this.archiveService.archive(resourceIds, []);
  }

  getRestoreResponse(resourceIds: string[]): Observable<IApiResponseWrapper<BulkUpdateResponse>> {
    return this.restoreService.restore(resourceIds, []);
  }

  filterArchivableResources(resources: IResource[], user: IUser): IResource[] {
    const archivaleRuns: IRun[] = super.filterArchivableResources(resources, user);
    return archivaleRuns.filter(resource => ARCHIVABLE_RUN_STATUSES.includes((resource as IRun).Status));
  }

  public canShare(): boolean {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return false;
    }

    if (this.isTransferInProgress()) {
      return false;
    }

    const { currentUserId } = this.getState();
    return this.shareService.canShare(activeRuns[0].UserOwnedBy, currentUserId);
  }

  public sendInvitations(collaborators: ICollaborator[], includeAssociatedResources: boolean): void {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return;
    }

    this.loadingSubject.next(true);
    this.subs.sink = this.shareService.sendInvitations(ResourceType.RUN, activeRuns[0].V1Pre3Id, collaborators).pipe(
      switchMap(() => {
        if (includeAssociatedResources) {
          return this.sendAssociatedResourcesInvitations(collaborators);
        } else {
          return of(null);
        }
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      }))
      .subscribe({
        complete: () => {
          this.setState({ runStateError: null });
        },
        error: error => {
          this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        }
      });
  }

  private sendAssociatedResourcesInvitations(collaborators: ICollaborator[]): Observable<IGenericResponse[]> {
    return this.getAvailableProjects().pipe(
      switchMap(projects => {
        //send project invite
        const observable$ = projects.map(project => this.shareService.sendInvitations(ResourceType.PROJECT, project.Id, collaborators));
        return forkJoin(observable$);
      })
    )
  }

  public getRunRequeueable(): Observable<IRequeueableResponse> {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return of({
        Message: 'You can only requeue one run at a time',
        Success: false
      });
    }

    this.loadingSubject.next(true);
    return this.bsApi.getRunRequeueable(activeRuns[0].Id).pipe(
      finalize(() => this.loadingSubject.next(false))
    );
  }

  public canRequeue(): boolean {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return false;
    }

    const { currentUserId } = this.getState();
    return this.requeueService.canRequeue(activeRuns[0], currentUserId);
  }

  public openRequeueSampleSheetPage() {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return false;
    }

    this.requeueService.openRequeueSampleSheetPage(activeRuns[0]);
  }

  public requeueSampleSheetWithoutValidation(sampleSheetContent: string) {
    this.loadingSubject.next(true);
    const activeRuns = this.getActiveList();
    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return;
    }
    this.requeueService.requeueRunWithoutValidation(activeRuns[0], sampleSheetContent)
      .subscribe(
        (runRequeueResponse: any) => {
          if (runRequeueResponse.Success) {
            this.toastrService.success('Requeue run was started successfully for analysis.');
          } else {
            this.toastrService.error(runRequeueResponse.Message, 'Error encountered for requeue run.');
          }
          this.loadingSubject.next(false);
        },
        (err) => {
          this.toastrService.error(err.Message || err.message, 'Error encountered for requeue run.');
          this.loadingSubject.next(false);
        });
  }

  public validateAndTransformSampleSheet(sampleSheetContent: string): Observable<[ValidateSampleSheetResponse, TransformSampleSheetResponse]> {
    this.bsSpinnerService.loadingSubject.next(true);
    return forkJoin([
      this.gssApiService.validateSampleSheet(sampleSheetContent),
      this.gssApiService.getRunFromSampleSheet(sampleSheetContent) // calls gss.transformSampleSheet
    ]).pipe(
      finalize(() => this.bsSpinnerService.loadingSubject.next(false))
    )
  }

  public getRunSampleSheetContent(): Observable<string> {
    this.bsSpinnerService.loadingSubject.next(true);

    const currentRun = this.getCurrentRun();
    if (!currentRun) {
      this.bsSpinnerService.loadingSubject.next(false);
      this.toastrService.error('Run is not found');
      return of(null);
    }

    const sampleSheetName = Constants.SAMPLE_SHEET_FILENAME;
    const params: BasespaceService.GetV2RunsIdFilesParams = {
      recursive: false,
      filehrefcontentresolution: false,
      sortdir: 'Asc',
      sortby: 'Name',
      limit: 2000,
      id: currentRun.V1Pre3Id,
      directory: '/'
    };

    return this.basespaceApi.GetV2RunsIdFiles(params).pipe(
      switchMap((res: V2FilesList) => {
        const sampleSheetV2File = res.Items.find(x => x.Name === sampleSheetName);
        if (sampleSheetV2File) {
          const contentHref = sampleSheetV2File.HrefContent + '?redirect=proxy';
          return this.httpClient.get(contentHref, { responseType: 'text', withCredentials: false });
        }
        return of('');
      }),
      map((sampleSheetContent: string) => {
        if (isEmpty(sampleSheetContent)) {
          this.toastrService.error("Sample sheet from this run is not found.");
          return null;
        }
        return sampleSheetContent;
      }),
      catchError(_ => {
        this.toastrService.error("Unable to get sample sheet from this run.");
        return of(null);
      }),
      finalize(() => this.bsSpinnerService.loadingSubject.next(false))
    );
  }

  public getCurrentRun(): IRun {
    const currentRuns = this.getActiveList();
    if (!currentRuns || isEmpty(currentRuns)) {
      return null;
    }
    return this.getActiveList()[0];
  }

  public getCollaborators(): Observable<ICollaborator[]> {
    const subject = new Subject<ICollaborator[]>();
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return EMPTY;
    }

    this.loadingSubject.next(true);
    this.subs.sink = this.shareService.getCollaborators(ResourceType.RUN, activeRuns[0].V1Pre3Id).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }))
      .subscribe({
        next: (apiResponse: ICollaboratorListResponse) => {
          subject.next(apiResponse.results);
          this.setState({ runStateError: null });
        },
        error: error => {
          subject.next([]);
          this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        }
      });

    return subject.asObservable();
  }

  public getShareLink(): Observable<string> {
    const subject = new Subject<string>();
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return EMPTY;
    }

    this.loadingSubject.next(true);
    this.subs.sink = this.shareService.getLink(ResourceType.RUN, activeRuns[0].V1Pre3Id).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }))
      .subscribe({
        next: (apiResponse: IShareUrlResponse) => {
          subject.next(apiResponse.ShareUrl);
          this.setState({ runStateError: null });
        },
        error: error => {
          subject.error(error);
          this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        }
      });

    return subject.asObservable();
  }

  public activateLink(): Observable<string> {
    const subject = new Subject<string>();
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return EMPTY;
    }

    this.loadingSubject.next(true);
    this.subs.sink = this.shareService.activateLink(ResourceType.RUN, activeRuns[0].V1Pre3Id).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }))
      .subscribe({
        next: (apiResponse: IShareUrlResponse) => {
          subject.next(apiResponse.ShareUrl);
          this.setState({ runStateError: null });
        },
        error: error => {
          this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        }
      });

    return subject.asObservable();
  }

  public deactivateLink(): Observable<string> {
    const subject = new Subject<string>();
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return EMPTY;
    }

    this.loadingSubject.next(true);
    this.subs.sink = this.shareService.deactivateLink(ResourceType.RUN, activeRuns[0].V1Pre3Id).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }))
      .subscribe({
        next: (apiResponse: IShareUrlResponse) => {
          subject.next(apiResponse.ShareUrl);
          this.setState({ runStateError: null });
        },
        error: error => {
          this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        }
      });

    return subject.asObservable();
  }

  public canRestore(): boolean {
    return this.isRestoreEnabled();
  }

  public restore(): void {
    const restorableRuns: IRun[] = this.getRestorableResources() as IRun[];
    const restoreObservable = this.restoreResources(restorableRuns);
    this.subs.sink = restoreObservable.subscribe({
      next: (apiResponse: IApiResponseWrapper<BulkUpdateResponse>) => {
        const proccessedResponse = this.processApiResponse(restorableRuns, apiResponse,
          ErrorMessages.FAILED_TO_RESTORE_RUNS);
        if (proccessedResponse.totalSuccessCount > 0) {
          // display toast on success
          this.toastrService.success(ModalTexts.RESTORE.TOAST_SUCCESS.RUN
            .replace('$SUCCESS_COUNT', apiResponse.Response.TotalSuccessCount.toString()));
        }

        this.setStateOnSuccess({ IsArchived: false, DataStatuses: [FileStatus.ACTIVE, FileStatus.UNARCHIVING, FileStatus.ARCHIVED] }, proccessedResponse.successfulResources as IRun[],
           RunStoreActions.RestoreRun, proccessedResponse.failureError, this.pageContextService.isRunMasterList());
      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
      }
    });
    return;
  }

  canUnzipRun(): boolean {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return false;
    }

    const { currentUserId } = this.getState();
    return this.unzipService.canUnzip(activeRuns[0], currentUserId);
  }

  unzipRun(): void {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return;
    }

    this.loadingSubject.next(true);
    this.subs.sink = this.unzipService.unzip(activeRuns[0]).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }))
      .subscribe({
        next: (response: IUnzipRunResponse) => {
          this.setStateOnSuccess({ IsUnzipping: true, IsZipped: false},
            activeRuns, RunStoreActions.UnzipRun, null, this.pageContextService.isRunMasterList());
        },
        error: error => {
          this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        }
      });
  }

  /**
   * TODO: accept params to make it generic
   * TODO: convert to state
   */
  public listRunsFiles(): Observable<V2FilesList> {
    const activeRuns = this.getActiveList();

    // applicable only in details page, i.e., to current run, so no need to check for selectedRuns
    if (!activeRuns[0]) {
      throwError('Run is still loading');
    }

    const params: BasespaceService.GetV2RunsIdFilesParams = {
      id: activeRuns[0].Id,
      recursive: false,
      filehrefcontentresolution: false,
      include: ['TotalCount']
    };

    this.loadingSubject.next(true);
    return this.basespaceApi.GetV2RunsIdFiles(params).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  /**
   * load Sequencingstats with more info
   * TODO: convert to state
   */
  public loadRunsSequencingStats(): Observable<V2SequencingStats> {
    const activeRuns = this.getActiveList();

    // applicable only in details page, i.e., to current run, so no need to check for selectedRuns
    if (!activeRuns[0]) {
      throwError('Run is still loading');
    }

    this.loadingSubject.next(true);
    return this.basespaceApi.GetV2RunsIdSequencingstats(activeRuns[0].Id).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  /**
   * Determines whether the currentRun can be download , according the store's state.
   */
  canDownload(): boolean {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return false;
    }

    if (this.isTransferInProgress()) {
      return false;
    }

    return this.downloadService.canDownload(activeRuns[0]) && !NON_DOWNLOADABLE_RUN_STATUSES.includes(activeRuns[0].Status);
  }

  /**
   * Calls BSSH API to get Run Download Uri.
   */
  getDownloadUri(): Observable<DownloadUrl> {
    const activeRuns = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activeRuns, 1)) {
      return EMPTY;
    }

    this.loadingSubject.next(true);
    return this.downloadService.getDownloadUri([activeRuns[0].V1Pre3Id], ResourceType.RUN).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  /**
   * Calls BSSH API to get Run Download Information. Only called from the run-details pages,
   * need not handle other page-contextsv
   * @param runId: id of the run
   */
  getDownloadInfo(runId: string): Observable<IApiResponseWrapper<IResource>> {
    this.loadingSubject.next(true);
    return this.downloadService.getDownloadInfo(runId, ResourceType.RUNS).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  private isRunOwner(currentUserId: string, run: IRun) {
    return run.UserOwnedBy.Id === currentUserId;
  }

  private isRunsOwner(currentUserId: string, runs: IRun[]): boolean {
    if (!runs || runs.length === 0 || !currentUserId) {
      return false;
    }

    return !runs.some((run) => {
      return !this.isRunOwner(currentUserId, run);
    });
  }

  /**
   * Builds Request for calling v1 APIs
   */
  // tslint:disable-next-line: ban-types
  private v1ApiRequestOptions(): Object {
    const headerArray = new HttpHeaders({ 'Content-Type': 'application/json; charset=utf-8' });
    return {
      headers: headerArray,
      withCredentials: true
    };
  }

  /**
   * Determines whether the currentUser can Trash the runs.
   */
  canTrash(): boolean {
    const activeRuns = this.getActiveList();
    if (this.isTransferInProgress()) {
      return false;
    }
    const { currentUserId } = this.getState();

    return this.trashService.canTrash(activeRuns, currentUserId);
  }

  /**
   * Calls the TrashService to Trash the Runs after checking the status
   * @param deleteOption Option to trash the Data Files only
   */
  trash(deleteOption?: DeleteOption): void {

    const activeRuns = this.getActiveList();
    const { currentUserId } = this.getState();
    const isOwnerOfRuns = this.isRunsOwner(currentUserId, activeRuns);

    if (!activeRuns) {
      throw new Error(ErrorMessages.MISSING_RESOURCE_ERROR);
    }

    this.loadingSubject.next(true);

    const runIds = [];
    activeRuns.forEach(runs => {
      runIds.push(runs.V1Pre3Id);
    });

    this.trashService.trashResources(runIds, ResourceType.RUNS, deleteOption).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (apiResponses) => {
        const totalSuccessCount = apiResponses.TotalSuccessCount;
        const totalFailureCount = apiResponses.TotalFailureCount;
        // getting runs to update that were successfully trashed
        const deletedRuns = [];
        if (totalSuccessCount > 0) {
          apiResponses.SuccessItems.forEach(item => {
            deletedRuns.push(item.Response.DeletedRun);
          });
        }

        // all successfully deleted runs will have same values for IsFileDataDeleted and IsDeleted
        const isFileDataDeleted = deletedRuns.length !== 0 ? deletedRuns[0].IsFileDataDeleted : false;
        const isDeleted = deletedRuns.length !== 0 ? deletedRuns[0].IsDeleted : false;

        // getting all error information for runs that failed deletion
        let trashError = null;
        if (totalFailureCount > 0) {
          trashError = [];
          apiResponses.FailureItems.forEach(item => {
            trashError.push({
              errorMessage: item.ErrorMessage,
              data: item,
            });
          });
        }

        if (deleteOption === DeleteOption.ASSOC_APPSESSIONS &&
          apiResponses.TotalSuccessCount === runIds.length) {
          // trashing appsession when all the runs are successfully trashed.
          this.trashAppSessions(runIds, isFileDataDeleted, isDeleted, deletedRuns, isOwnerOfRuns);
        } else if (deleteOption === DeleteOption.PRESERVE_METADATA) {
          this.setStateOnSuccess({ IsFileDataDeleted: isFileDataDeleted },
            deletedRuns, RunStoreActions.DeleteRun, trashError);
        } else {
          this.setStateOnSuccess({
            IsDeleted: isDeleted,
            IsFileDataDeleted: isFileDataDeleted
          }, deletedRuns,
            RunStoreActions.DeleteRun, trashError,
            this.pageContextService.isRunMasterList());
        }

        // For showing toastr message
        // show runs related trash complete toastr message when appSessions option is not selected
        if (deleteOption !== DeleteOption.ASSOC_APPSESSIONS && (!trashError || trashError.length === 0)) {
          const multipleRunsDeleted = totalSuccessCount > 1 ? true : false;
          this.toastrService.success(ToastrMessages.TRASH_COMPLETE_MESSAGE(deleteOption, isOwnerOfRuns, multipleRunsDeleted));
        }

      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        this.loadingSubject.next(false);
      }
    });
  }

  /**
   * get app sessions for runs and then delete those app sessions
   */
  private trashAppSessions(runIds: string[], isFileDataDeleted: boolean, isDeleted: boolean, deletedRuns: IRun[],
    isOwnerOfRuns: boolean) {

    // get app sessions for runs ids
    this.trashService.getAppSessionsForRun(runIds).subscribe({
      next: (appSessionsResponse) => {

        const appSessionsIds = [];
        appSessionsResponse.Items.forEach(item => {
          appSessionsIds.push(item.Id);
        });

        // trash app sessions of recieved ids
        this.trashService.trashAppSessions(appSessionsIds).subscribe({
          next: (appSessionTrashResponse) => {

            let trashError = null;
            if (appSessionTrashResponse.TotalFailureCount > 0) {
              trashError = [];

              // load trash error with app sessions error as runs are already successful,
              // only app session errors will be there
              appSessionTrashResponse.FailureItems.forEach(item => {
                trashError.push({
                  errorMessage: item.ErrorMessage,
                  data: item,
                });
              });

            }

            this.setStateOnSuccess({
              IsDeleted: isDeleted,
              IsFileDataDeleted: isFileDataDeleted
            }, deletedRuns,
              RunStoreActions.DeleteRun, trashError,
              this.pageContextService.isRunMasterList());

            // show toastr message when appSessions option is selected
            if (!trashError || trashError.length === 0) {
              const multipleRunsDeleted = appSessionTrashResponse.TotalSuccessCount > 1 ? true : false;
              this.toastrService.success(ToastrMessages.TRASH_COMPLETE_MESSAGE(DeleteOption.ASSOC_APPSESSIONS, isOwnerOfRuns,
                multipleRunsDeleted));
            }

          },
          error: error => {
            this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
            this.loadingSubject.next(false);
          }
        });
      },
      error: error => {
        this.handleError(error, () => ({ ...this.getState(), runStateError: error }));
        this.loadingSubject.next(false);
      }
    });
  }

  getAvailableAppSessions(): Observable<V2AppSessionCompactList> {
    const activeRuns = this.getActiveList();

    const ids = [];
    activeRuns.forEach(run => {
      ids.push(Number(run.V1Pre3Id));
    });

    const requestParams: BasespaceService.GetV2AppsessionsParams = {
      inputRuns: ids,
      include: ['Properties'],
      propertyfilters: ['output.projects']
    };

    return this.basespaceApi.GetV2Appsessions(requestParams).pipe(
      // Retry in case of HTTP errors
      retryWhen(genericRetryWhen()),
      // To avoid a Flash of content, maintain a delay
      delay(observableEmitDelay),
    );
  }

  getAvailableProjects(){
    return this.getAvailableAppSessions().pipe(
      switchMap(response => {
        return this.toAvailableAppsessionProjects(response);
      })
    );
  }

  private toAvailableAppsessionProjects(result: V2AppSessionCompactList): Observable<V1pre3ProjectCompact[]> {
    const appsessions = result.Items;
    let projects: V1pre3ProjectCompact[]  = []
    appsessions.forEach(items => {
      if (items && items.Properties && items.Properties.Items) {
        const properties = items.Properties.Items;
        properties.forEach(item => {
          if (item.ProjectItems) {
            //Do not share projects named "Unindexed Reads".
            projects = item.ProjectItems.filter(items =>  items.Name !== Constants.UNINDEXED_READS_PROJECT_NAME);
          }
        });
      }
    });
   return of(projects);
  }

  getResourceType(): ResourceType {
    return ResourceType.RUNS;
  }

  canInstrumentSupportDemuxMetrics(): boolean {
    const { currentRun } = { ...this.getState() };
    const instrumentPlatform = currentRun && currentRun.Instrument ? 
      InstrumentTypes.ToPlatform(currentRun.Instrument.Type) : InstrumentTypes.UNKNOWN;
    return !this.DEMUX_UNSUPPORTED_INSTRUMENT_PLATFORMS.includes(instrumentPlatform);
  }

  /**
   * Check code feature to determine if demux metrics is enabled.
   * e.g. ignoreInstrumentSequencingStats is enabled -> interop seq stats is used
   *   -> since interop seq stats do not contain demux, no demux metrics will be available.
   *   -> demux metrics is effectively 'disabled'
   */
  isDemuxMetricsEnabled(): boolean {
    const { currentRun } = { ...this.getState() };
    const instrumentType = currentRun.Instrument.Type
    const instrumentPlatform = InstrumentTypes.ToPlatform(instrumentType);
    return !this.codeFeatureService.ignoreInstrumentSequencingStatsEnabled(instrumentPlatform);
  }
}

// The action-names for this store, for logging/tracking.
export enum RunStoreActions {
  LoadRunDetails = 'LOAD_RUN',
  EditRunExpName = 'EDIT_RUN_NAME',
  TransferRunOwnership = 'TRANSFER_RUN_OWNERSHIP',
  CancelRunTransfer = 'CANCEL_RUN_OWNERSHIP_TRANSFER',
  DeleteRun = 'DELETE_RUN',
  RestoreRun = 'RESTORE_RUN',
  LoadRunsList = 'LOAD_RUNS_LIST',
  ArchiveRun = 'ARCHIVE_RUN',
  UpdateSelectedRuns = 'UPDATE_SELECTED_RUNS',
  UpdateInstrumentFilterList = 'UPDATE_INSTRUMENT_FILTER_LIST',
  UnloadCurrentRun = 'UNLOAD_CURRENT_RUN',
  UnzipRun = 'UNZIP_RUN'
}
